import {AugmentedDashboardWidgetEntry, DashboardWidgetEntry, DashboardWidgetType} from "./types";
import Api from "../../../../../../core/services/api/api";
import {CancelToken} from "axios";
import {DashboardWidgetConfig, DashboardWidgetIdentifiers, DashboardWidgetVariants} from "./dashboard-widget.config";

interface StaticDashboardWidgetEntry extends AugmentedDashboardWidgetEntry {
}

interface CategorizedStaticDashboardWidgets {
    category: string;
    widgets: StaticDashboardWidgetEntry[];
}

class StaticDashboardWidgetGenerator {

    /**
     * Fetches all the dashboard widgets from the backend.
     * @param cancelToken
     * @private
     */
    private static async getAllWidgets(cancelToken?: CancelToken): Promise<DashboardWidgetEntry[]> {
        const response = await Api.getCustomDashboardsAllWidgets(cancelToken);
        if (cancelToken?.reason) {
            return [];
        }
        return response?.data ?? [];
    }

    /**
     * Generates the product information view widgets.
     * @param cancelToken
     */
    static async productInformationView(cancelToken?: CancelToken): Promise<CategorizedStaticDashboardWidgets[] | undefined> {
        const allWidgets = await this.getAllWidgets(cancelToken);
        if (cancelToken?.reason) {
            return;
        }

        const salesToday = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_todaySales);
        const salesThisWeek = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_wtdSales);
        const salesThisMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_mtdSales);
        const salesThisYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_ytdSales);
        const salesByCustomer = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_ytdCustomer);
        const lostSalesByCustomer = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_lostSalesCustomer);
        const salesByYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_annualSales);
        const salesByMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_monthlySales);
        const salesByProvince = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByProvince);
        const salesByCity = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByCity);
        const salesByPersonSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByAccountManager);

        const warrantiesByCustomer = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByCustomer);
        const warrantiesByVendor = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByVendor);
        const warrantiesByReason = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByReason);
        const warrantiesByStatus = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByStatus);

        const result: Record<"sales" | 'warranty', AugmentedDashboardWidgetEntry[]> = {
            sales: [],
            warranty: [],
        }
        const categoryTitles = {
            sales: "Sales Statistics",
            warranty: "Warranty & Returns Statistics",
        }

        // sales
        if (salesToday) {
            result.sales.push({
                ...salesToday,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, lg: 3},
                configuration: new DashboardWidgetConfig(salesToday.id).getDataConfig(),
            })
        }
        if (salesThisWeek) {
            result.sales.push({
                ...salesThisWeek,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, lg: 3},
                configuration: new DashboardWidgetConfig(salesThisWeek.id).getDataConfig(),
            })
        }
        if (salesThisMonth) {
            result.sales.push({
                ...salesThisMonth,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, lg: 3},
                configuration: new DashboardWidgetConfig(salesThisMonth.id).getDataConfig(),
            })
        }
        if (salesThisYear) {
            result.sales.push({
                ...salesThisYear,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, lg: 3},
                configuration: new DashboardWidgetConfig(salesThisYear.id).getDataConfig(),
            })
        }

        if (salesByCustomer) {
            result.sales.push({
                ...salesByCustomer,
                variant: DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByCustomer.id, DashboardWidgetVariants.salesYtdCustomerBarChartSalesBased).getDataConfig(),
            })
        }
        if (lostSalesByCustomer) {
            result.sales.push({
                ...lostSalesByCustomer,
                variant: DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(lostSalesByCustomer.id, DashboardWidgetVariants.salesLostSalesCustomerBarChartSalesBased).getDataConfig(),
            })
        }
        if (salesByYear) {
            result.sales.push({
                ...salesByYear,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByYear.id).getDataConfig(),
            })
        }
        if (salesByMonth) {
            result.sales.push({
                ...salesByMonth,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByMonth.id).getDataConfig(),
            })
        }
        if (salesByProvince) {
            result.sales.push({
                ...salesByProvince,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByProvince.id, DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased).getDataConfig(),
            })
        }
        if (salesByCity) {
            result.sales.push({
                ...salesByCity,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByCity.id, DashboardWidgetVariants.salesSalesByCityBarChartSalesBased).getDataConfig(),
            })
        }
        if (salesByPersonSales) {
            result.sales.push({
                ...salesByPersonSales,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(salesByPersonSales.id, DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased).getDataConfig(),
            })
        }

        // warranty
        if (warrantiesByCustomer) {
            result.warranty.push({
                ...warrantiesByCustomer,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(warrantiesByCustomer.id).getDataConfig(),
            })
        }
        if (warrantiesByVendor) {
            result.warranty.push({
                ...warrantiesByVendor,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(warrantiesByVendor.id).getDataConfig(),
            })
        }
        if (warrantiesByReason) {
            result.warranty.push({
                ...warrantiesByReason,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(warrantiesByReason.id).getDataConfig(),
            })
        }
        if (warrantiesByStatus) {
            result.warranty.push({
                ...warrantiesByStatus,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, lg: 6},
                configuration: new DashboardWidgetConfig(warrantiesByStatus.id).getDataConfig(),
            })
        }

        return Object
            .entries(result)
            .filter(e => e[1].length > 0)
            .map(([category, widgets]) => ({
                category: categoryTitles[category] ?? category,
                widgets: widgets.map(w => ({
                    ...w,
                    connectionId: w.connectionId || w.id,
                })),
            }));
    }


    static async customerInformationView(cancelToken?: CancelToken): Promise<CategorizedStaticDashboardWidgets[] | undefined> {
        const allWidgets = await this.getAllWidgets(cancelToken);
        if (cancelToken?.reason) {
            return;
        }

        const salesToday = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_todaySales);
        const salesThisWeek = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_wtdSales);
        const salesThisMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_mtdSales);
        const salesThisYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_ytdSales);
        const salesByPart = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_ytdParts);
        const lostSalesByPart = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_lostSalesParts);
        const salesByYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_annualSales);
        const salesByMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_monthlySales);
        const salesByProvince = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByProvince);
        const salesByCity = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByCity);
        const salesByPersonSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.sales_salesByAccountManager);

        const warrantiesByPartsAndSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByPartAndSales);
        const warrantiesByCategoryAndSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales);
        const warrantiesByVendor = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByVendor);
        const warrantiesByReason = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByReason);
        const warrantiesByStatus = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByStatus);

        const result: Record<"sales" | 'warranty', AugmentedDashboardWidgetEntry[]> = {
            sales: [],
            warranty: [],
        }
        const categoryTitles = {
            sales: "Sales Statistics",
            warranty: "Warranty & Returns Statistics",
        }

        // sales
        if (salesToday) {
            result.sales.push({
                ...salesToday,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, xl: 3},
                configuration: new DashboardWidgetConfig(salesToday.id).getDataConfig(),
            })
        }
        if (salesThisWeek) {
            result.sales.push({
                ...salesThisWeek,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, xl: 3},
                configuration: new DashboardWidgetConfig(salesThisWeek.id).getDataConfig(),
            })
        }
        if (salesThisMonth) {
            result.sales.push({
                ...salesThisMonth,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, xl: 3},
                configuration: new DashboardWidgetConfig(salesThisMonth.id).getDataConfig(),
            })
        }
        if (salesThisYear) {
            result.sales.push({
                ...salesThisYear,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 6, xl: 3},
                configuration: new DashboardWidgetConfig(salesThisYear.id).getDataConfig(),
            })
        }
        if (salesByPart) {
            result.sales.push({
                ...salesByPart,
                variant: DashboardWidgetVariants.salesYtdPartsBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByPart.id, DashboardWidgetVariants.salesYtdPartsBarChartSalesBased).getDataConfig(),
            })
        }
        if (lostSalesByPart) {
            result.sales.push({
                ...lostSalesByPart,
                variant: DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(lostSalesByPart.id, DashboardWidgetVariants.salesLostSalesPartsBarChartSalesBased).getDataConfig(),
            })
        }
        if (salesByYear) {
            result.sales.push({
                ...salesByYear,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByYear.id).getDataConfig(),
            })
        }
        if (salesByMonth) {
            result.sales.push({
                ...salesByMonth,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByMonth.id).getDataConfig(),
            })
        }
        if (salesByProvince) {
            result.sales.push({
                ...salesByProvince,
                variant: DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByProvince.id, DashboardWidgetVariants.salesSalesByProvinceBarChartSalesBased).getDataConfig(),
            });
        }
        if (salesByCity) {
            result.sales.push({
                ...salesByCity,
                variant: DashboardWidgetVariants.salesSalesByCityBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByCity.id, DashboardWidgetVariants.salesSalesByCityBarChartSalesBased).getDataConfig(),
            })
        }
        if (salesByPersonSales) {
            result.sales.push({
                ...salesByPersonSales,
                variant: DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(salesByPersonSales.id, DashboardWidgetVariants.salesSalesByAccountManagerBarChartSalesBased).getDataConfig(),
            })
        }

        // warranty
        if (warrantiesByPartsAndSales) {
            result.warranty.push({
                ...warrantiesByPartsAndSales,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByPartsAndSales.id).getDataConfig(),
            })
        }
        if (warrantiesByCategoryAndSales) {
            result.warranty.push({
                ...warrantiesByCategoryAndSales,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByCategoryAndSales.id).getDataConfig(),
            })
        }
        if (warrantiesByVendor) {
            result.warranty.push({
                ...warrantiesByVendor,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByVendor.id).getDataConfig(),
            })
        }
        if (warrantiesByReason) {
            result.warranty.push({
                ...warrantiesByReason,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByReason.id).getDataConfig(),
            })
        }
        if (warrantiesByStatus) {
            result.warranty.push({
                ...warrantiesByStatus,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByStatus.id).getDataConfig(),
            })
        }

        return Object
            .entries(result)
            .filter(e => e[1].length > 0)
            .map(([category, widgets]) => ({
                category: categoryTitles[category] ?? category,
                widgets: widgets.map(w => ({
                    ...w,
                    connectionId: w.connectionId || w.id,
                })),
            }));
    }

    static async vendorInformationView(cancelToken?: CancelToken): Promise<CategorizedStaticDashboardWidgets[] | undefined> {
        const allWidgets = await this.getAllWidgets(cancelToken);
        if (cancelToken?.reason) {
            return;
        }

        const purchaseThisWeek = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_wtdPurchases);
        const purchaseThisMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_mtdPurchases);
        const purchaseThisYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_ytdPurchases);
        const purchaseByPart = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_ytdParts);
        const purchaseByYear = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_annualPurchases);
        const purchaseByMonth = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_monthlyPurchases);
        const purchaseYtdAnnual = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_ytdAnnualPurchases);
        const purchaseByCurrency = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_purchaseByCurrency);
        const purchaseByTerritory = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_purchaseByTerritory);
        const purchaseByWarehouse = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.purchase_purchaseByWarehouse);

        const warrantiesByPartsAndSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByPartAndSales);
        const warrantiesByCategoryAndSales = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByCategoryAndSales);
        const warrantiesByCustomer = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByCustomer);
        const warrantiesByReason = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByReason);
        const warrantiesByStatus = allWidgets.find(e => e.id === DashboardWidgetIdentifiers.warranty_requestsByStatus);

        const result: Record<"purchase" | "warranty", AugmentedDashboardWidgetEntry[]> = {
            purchase: [],
            warranty: [],
        }
        const categoryTitles = {
            purchase: "Purchase Statistics",
            warranty: "Warranty & Returns Statistics",
        }

        // purchase — 3 info blocks (WTD / MTD / YTD) at 4 cols on large screens
        if (purchaseThisWeek) {
            result.purchase.push({
                ...purchaseThisWeek,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 12, xl: 4},
                configuration: new DashboardWidgetConfig(purchaseThisWeek.id).getDataConfig(),
            })
        }
        if (purchaseThisMonth) {
            result.purchase.push({
                ...purchaseThisMonth,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 12, xl: 4},
                configuration: new DashboardWidgetConfig(purchaseThisMonth.id).getDataConfig(),
            })
        }
        if (purchaseThisYear) {
            result.purchase.push({
                ...purchaseThisYear,
                type: DashboardWidgetType.infoBlock,
                size: {xs: 12, xl: 4},
                configuration: new DashboardWidgetConfig(purchaseThisYear.id).getDataConfig(),
            })
        }
        // Parts → Monthly → Annual → YTD Annual (full width)
        if (purchaseByPart) {
            result.purchase.push({
                ...purchaseByPart,
                variant: DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByPart.id, DashboardWidgetVariants.purchaseYtdPartsBarChartCostBased).getDataConfig(),
            })
        }
        if (purchaseByMonth) {
            result.purchase.push({
                ...purchaseByMonth,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByMonth.id).getDataConfig(),
            })
        }
        if (purchaseByYear) {
            result.purchase.push({
                ...purchaseByYear,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByYear.id).getDataConfig(),
            })
        }
        if (purchaseYtdAnnual) {
            result.purchase.push({
                ...purchaseYtdAnnual,
                type: DashboardWidgetType.lineChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseYtdAnnual.id).getDataConfig(),
            })
        }
        if (purchaseByCurrency) {
            result.purchase.push({
                ...purchaseByCurrency,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByCurrency.id).getDataConfig(),
            })
        }
        if (purchaseByTerritory) {
            result.purchase.push({
                ...purchaseByTerritory,
                variant: DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByTerritory.id, DashboardWidgetVariants.purchasePurchaseByTerritoryBarChartCostBased).getDataConfig(),
            })
        }
        if (purchaseByWarehouse) {
            result.purchase.push({
                ...purchaseByWarehouse,
                variant: DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(purchaseByWarehouse.id, DashboardWidgetVariants.purchasePurchaseByWarehouseBarChartCostBased).getDataConfig(),
            })
        }

        // warranty (requestsByCustomer instead of requestsByVendor — single vendor context)
        if (warrantiesByPartsAndSales) {
            result.warranty.push({
                ...warrantiesByPartsAndSales,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByPartsAndSales.id).getDataConfig(),
            })
        }
        if (warrantiesByCategoryAndSales) {
            result.warranty.push({
                ...warrantiesByCategoryAndSales,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByCategoryAndSales.id).getDataConfig(),
            })
        }
        if (warrantiesByCustomer) {
            result.warranty.push({
                ...warrantiesByCustomer,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByCustomer.id).getDataConfig(),
            })
        }
        if (warrantiesByReason) {
            result.warranty.push({
                ...warrantiesByReason,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByReason.id).getDataConfig(),
            })
        }
        if (warrantiesByStatus) {
            result.warranty.push({
                ...warrantiesByStatus,
                type: DashboardWidgetType.barChart,
                size: {xs: 12, xl: 6},
                configuration: new DashboardWidgetConfig(warrantiesByStatus.id).getDataConfig(),
            })
        }

        return Object
            .entries(result)
            .filter(e => e[1].length > 0)
            .map(([category, widgets]) => ({
                category: categoryTitles[category] ?? category,
                widgets: widgets.map(w => ({
                    ...w,
                    connectionId: w.connectionId || w.id,
                })),
            }));
    }

}

export default StaticDashboardWidgetGenerator;